"use client";

import { FC, MouseEvent, useState, useEffect } from "react";
import { FcGoogle } from "react-icons/fc";
import Button from "@mui/material/Button";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Theme } from "@mui/material/styles";

import { useSessionStorage } from "@/hooks/useStorage";
import { useStyles } from "./styles";
import Modal from "@/components/common/Modal";

interface GoogleButtonProps {
  actionType: "login" | "register";
  subscribeToEmails?: boolean;
  disabled?: boolean;
  className?: string;
  onSuccess?: () => void | Promise<void>;
}

const GoogleButton: FC<GoogleButtonProps> = ({
  actionType,
  disabled = false,
  subscribeToEmails = true,
  className,
  onSuccess,
}) => {
  const { classes, cx } = useStyles({ disabled });
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const [showModal, setShowModal] = useState(false);
  const [isEmbeddedBrowser, setIsEmbeddedBrowser] = useState(false);
  const [googleAds] = useSessionStorage("GoogleAds", false);

  const emailSubscriptionQuery =
    actionType === "register" && subscribeToEmails ? "&subscribe=true" : "";

  const handleLinkClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (!isEmbeddedBrowser) return;
    e.preventDefault();
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleOpenInBrowser = () => {
    const url = `${process.env.NEXT_PUBLIC_API_URL}/auth/google?client_redirect_url=${process.env.NEXT_PUBLIC_BASE_URL}${emailSubscriptionQuery}`;
    window.open(url, "_system");
  };

  const gtag_report_conversion = () => {
    (window as any).gtag("event", "conversion", {
      send_to: "AW-11397301260/_bvGCOu5rtsZEIyI1Loq",
      value: 0.1,
      currency: "AUD",
    });
  };

  const handleSubmit = async () => {
    if (googleAds && actionType === "register") {
      gtag_report_conversion();
    }

    // Use localStorage instead of sessionStorage (survives navigation)
    if (onSuccess) {
      localStorage.setItem("oauthSuccessCallback", "google");
      localStorage.setItem("oauthInitiatedAt", Date.now().toString());
    }
  };

  useEffect(() => {
    // Check if the user is using an embedded browser / webviewer (Google OAuth won't work in webviewers)
    if (typeof navigator === undefined || typeof window === undefined) return;
    const ua = navigator.userAgent;
    setIsEmbeddedBrowser(/FBAN|FBAV|Instagram|Line|Twitter|Messenger/.test(ua));
  }, []);

  // FIXME: Show Google button again once we're able to open the link in default browser
  if (isEmbeddedBrowser) return null;

  return (
    <>
      <a
        href={`${process.env.NEXT_PUBLIC_API_URL}/auth/google?client_redirect_url=${process.env.NEXT_PUBLIC_BASE_URL}${emailSubscriptionQuery}`}
        className={classes.link}
        onClick={handleLinkClick}
      >
        <Button
          variant="contained"
          className={cx(classes.googleButton, className)}
          disabled={disabled}
          onClick={handleSubmit}
        >
          <FcGoogle className={classes.googleIcon} />
          Sign {actionType === "login" ? "in" : "up"} with Google
        </Button>
      </a>
      <Modal open={showModal} onClose={handleCloseModal}>
        <div className={classes.modalContent}>
          <h1>Open in Browser</h1>
          <p>
            Please open this link in your default browser to continue with the
            sign-in process.
          </p>
          <Button variant="outlined" onClick={handleOpenInBrowser}>
            {isMobile ? "Tap" : "Click"} here to open in browser
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default GoogleButton;
